import { create } from 'zustand';
import publicApi from '../services/publicApi';

let autoSaveTimer = null;

const useQuizCodeStore = create((set, get) => ({
    // State
    code: null,
    participantName: null,
    sousSujet: null,
    design: null, // { scope_label, couleur_theme, header_image }
    mode: 'quiz', // 'quiz' | 'apprentissage' | 'sealed'
    questions: [],
    questionIds: [],
    answers: {},
    correctAnswers: null,
    answersSnapshot: null,
    currentIndex: 0,
    timeInSeconds: 0,
    loading: false,
    finished: false,
    sealed: false, // true when session is sealed and results are locked
    score: null,
    total: null,
    email: null,             // effective notification email (participant ?? invitation)
    emailInvitation: null,   // original invitation email set by the owner (read-only)
    participantEmail: null,  // address set by the participant, if any
    emailLocked: false,      // true if the flow disallows further changes (e.g. score already sent)
    error: null,

    // Start or resume quiz
    startQuiz: async (code, participantName, email) => {
        set({ loading: true, error: null, finished: false, sealed: false, correctAnswers: null, answersSnapshot: null });
        try {
            const { data } = await publicApi.post('/quiz-code/start.php', {
                code: code.toUpperCase(),
                participant_name: participantName || undefined,
                email: email || undefined,
            });
            const d = data.data;

            // Need-name : code is AVAILABLE and nothing has started yet. Save
            // design + any pre-filled email so the enter-name form can use it.
            if (d.status === 'need-name') {
                set({
                    code: code.toUpperCase(),
                    sousSujet: d.sous_sujet,
                    design: d.design || null,
                    mode: d.mode || 'quiz',
                    email: d.email || null,
                    emailInvitation: d.email_invitation || null,
                    participantEmail: d.participant_email || null,
                    emailLocked: !!d.email_locked,
                    loading: false,
                });
                return 'need-name';
            }

            // Sealed mode : session finished but review is locked by the trainer.
            if (d.status === 'sealed') {
                set({
                    code: code.toUpperCase(),
                    participantName: d.participant_name,
                    sousSujet: d.sous_sujet,
                    design: d.design || null,
                    mode: 'sealed',
                    finished: true,
                    sealed: true,
                    email: d.email || null,
                    emailInvitation: d.email_invitation || null,
                    participantEmail: d.participant_email || null,
                    emailLocked: !!d.email_locked,
                    loading: false,
                });
                return true;
            }

            // Shuffle responses for new sessions (not resume)
            const questions = (d.questions || []).map(q => {
                if (d.status === 'new' && Array.isArray(q.reponses)) {
                    q.reponses = [...q.reponses].sort(() => Math.random() - 0.5);
                }
                return q;
            });

            // Review mode: finished quiz, show results
            if (d.status === 'review') {
                // Build correctAnswers from questions (which include bonne_reponse in review)
                const correctAnswersMap = {};
                questions.forEach(q => {
                    correctAnswersMap[q.id] = {
                        correct: q.bonne_reponse,
                        explication: q.explication,
                    };
                });
                // Build answers from snapshot
                const reviewAnswers = {};
                if (d.answers_snapshot) {
                    Object.entries(d.answers_snapshot).forEach(([qId, snap]) => {
                        reviewAnswers[qId] = snap.given;
                    });
                }

                set({
                    code: code.toUpperCase(),
                    participantName: d.participant_name,
                    sousSujet: d.sous_sujet,
                    design: d.design || null,
                    mode: d.mode || 'quiz',
                    questions,
                    questionIds: d.question_ids || [],
                    answers: reviewAnswers,
                    answersSnapshot: d.answers_snapshot || null,
                    correctAnswers: correctAnswersMap,
                    currentIndex: 0,
                    timeInSeconds: d.time_in_seconds || 0,
                    score: d.score,
                    total: d.total || questions.length,
                    email: d.email || null,
                    emailInvitation: d.email_invitation || null,
                    participantEmail: d.participant_email || null,
                    emailLocked: !!d.email_locked,
                    finished: true,
                    loading: false,
                });
                return true;
            }

            set({
                code: code.toUpperCase(),
                participantName: d.participant_name,
                sousSujet: d.sous_sujet,
                design: d.design || null,
                mode: d.mode || 'quiz',
                correctAnswers: d.mode === 'apprentissage' ? (d.correct_answers || {}) : null,
                questions,
                questionIds: d.question_ids || [],
                answers: d.answers || {},
                currentIndex: d.current_question_index || 0,
                timeInSeconds: d.time_in_seconds || 0,
                total: questions.length,
                email: d.email || null,
                emailInvitation: d.email_invitation || null,
                participantEmail: d.participant_email || null,
                emailLocked: !!d.email_locked,
                loading: false,
            });

            // Auto-save immediately for new sessions
            if (d.status === 'new') {
                get()._autoSave();
            }

            return true;
        } catch (err) {
            set({
                loading: false,
                error: err.response?.data?.message || 'Erreur de connexion',
            });
            return false;
        }
    },

    setAnswer: (questionId, answer) => {
        set(s => ({
            answers: { ...s.answers, [questionId]: answer },
        }));
        get()._debouncedAutoSave();

        // In apprentissage mode, fetch correct answer immediately
        if (get().mode === 'apprentissage') {
            get().checkAnswer(questionId);
        }
    },

    checkAnswer: async (questionId) => {
        const { code, correctAnswers } = get();
        if (!code || correctAnswers?.[questionId]) return; // already checked
        try {
            const { data } = await publicApi.post('/quiz-code/check-answer.php', { code, question_id: questionId });
            if (data.success) {
                set(s => ({
                    correctAnswers: { ...s.correctAnswers, [questionId]: data.data },
                }));
            }
        } catch {}
    },

    goToQuestion: (index) => {
        const { questions } = get();
        if (index >= 0 && index < questions.length) {
            set({ currentIndex: index });
        }
    },

    nextQuestion: () => {
        const { currentIndex, questions } = get();
        if (currentIndex < questions.length - 1) {
            set({ currentIndex: currentIndex + 1 });
        }
    },

    prevQuestion: () => {
        const { currentIndex } = get();
        if (currentIndex > 0) {
            set({ currentIndex: currentIndex - 1 });
        }
    },

    tick: () => {
        set(s => {
            const next = s.timeInSeconds + 1;
            // Auto-save every 30 seconds to persist timer progress
            if (next % 30 === 0) {
                setTimeout(() => get()._autoSave(), 0);
            }
            return { timeInSeconds: next };
        });
    },

    finishQuiz: async () => {
        const { code } = get();
        if (!code) return false;

        set({ loading: true, error: null });
        try {
            // Save final state first
            await get()._autoSave();

            const { data } = await publicApi.post('/quiz-code/finish.php', { code });
            const d = data.data;

            // Sealed : server hides score + correction until trainer releases.
            if (d.sealed) {
                set({
                    finished: true,
                    sealed: true,
                    loading: false,
                });
                return true;
            }

            set({
                finished: true,
                sealed: false,
                score: d.score,
                total: d.total,
                correctAnswers: d.answers,
                answersSnapshot: d.answers_snapshot,
                loading: false,
            });
            return true;
        } catch (err) {
            set({
                loading: false,
                error: err.response?.data?.message || 'Erreur',
            });
            return false;
        }
    },

    reset: () => {
        if (autoSaveTimer) clearTimeout(autoSaveTimer);
        set({
            code: null,
            participantName: null,
            sousSujet: null,
            design: null,
            mode: 'quiz',
            questions: [],
            questionIds: [],
            answers: {},
            correctAnswers: null,
            answersSnapshot: null,
            currentIndex: 0,
            timeInSeconds: 0,
            loading: false,
            finished: false,
            sealed: false,
            score: null,
            total: null,
            email: null,
            emailInvitation: null,
            participantEmail: null,
            emailLocked: false,
            error: null,
        });
    },

    // Update the opt-in notification email after the quiz has started.
    // Returns { success: bool, error?: string }.
    updateEmail: async (emailValue) => {
        const { code } = get();
        if (!code) return { success: false, error: 'code manquant' };
        try {
            const { data } = await publicApi.post('/quiz-code/update-email.php', {
                code,
                email: emailValue ?? '',
            });
            set({
                email: data.data?.email ?? null,
                emailInvitation: data.data?.email_invitation ?? null,
                participantEmail: data.data?.participant_email ?? null,
                emailLocked: !!data.data?.email_locked,
            });
            return { success: true };
        } catch (err) {
            return { success: false, error: err.response?.data?.message || 'Erreur' };
        }
    },

    _autoSave: async () => {
        const { code, answers, currentIndex, timeInSeconds, questionIds, finished } = get();
        if (!code || finished) return;
        try {
            await publicApi.post('/quiz-code/save.php', {
                code,
                answers,
                current_question_index: currentIndex,
                time_in_seconds: timeInSeconds,
                question_ids: questionIds,
            });
        } catch {}
    },

    _debouncedAutoSave: () => {
        if (autoSaveTimer) clearTimeout(autoSaveTimer);
        autoSaveTimer = setTimeout(() => get()._autoSave(), 1000);
    },
}));

export default useQuizCodeStore;
